import React, { useState, useEffect } from "react";
import axios from "axios";
import { auth, db } from "../../firebase";
import { doc, setDoc } from "firebase/firestore";
import styleClasses from "./css/MyPage.module.scss";

// 🔹 활동 정보를 가져오는 API 엔드포인트 (API 서버 URL은 상황에 맞게 수정)
const API_ACTIVITY_URL = "https://music-api-134893551192.asia-northeast3.run.app/api/activity/youtube";

function ImportActivity() {
  const [channelUrl, setChannelUrl] = useState(""); // 유튜브 채널 URL 입력값
  const [playlistUrl, setPlaylistUrl] = useState("");      // 플레이리스트 URL 입력값
  const [videoUrl, setVideoUrl] = useState("");            // 동영상 URL 입력값
  const [activities, setActivities] = useState([]);   // 가져온 활동 목록
  const [loading, setLoading] = useState(false);
  const [uid, setUid] = useState(null);
  const [authToken, setAuthToken] = useState(""); // Firebase Auth JWT 토큰

  // Firebase 로그인 상태 감지 및 토큰 저장
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUid(user.uid);
        const token = await user.getIdToken();
        setAuthToken(token);
      }
    });
    return () => unsubscribe();
  }, []);

  // 새로운 함수: 채널, 플레이리스트, 동영상 URL 중 하나를 사용하여 활동 가져오기  
  const fetchActivitiesFromYouTube = async () => {
    // 우선순위: 플레이리스트 URL > 동영상 URL > 채널 URL
    let params = {};
    if (playlistUrl) {
      params.playlistUrl = playlistUrl;
      console.log("🎤 플레이리스트 URL 입력:", playlistUrl);
    } else if (videoUrl) {
      params.videoUrl = videoUrl;
      console.log("🎤 동영상 URL 입력:", videoUrl);
    } else if (channelUrl) {
      params.channelUrl = channelUrl;
      console.log("🎤 채널 URL 입력:", channelUrl);
    } else {
      alert("유튜브 채널, 플레이리스트, 또는 동영상 URL을 입력하세요.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(API_ACTIVITY_URL, {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      });
      console.log("🎶 API 응답 데이터:", response.data);
      setActivities(response.data.activities);
    } catch (error) {
      console.error("❌ 활동 API 호출 실패:", error.response?.data || error.message);
      alert("활동 검색 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 채널 URL에서 '@' 뒤의 채널명을 추출하는 헬퍼 함수 (예: https://www.youtube.com/@skykyeng94 → skykyeng94)
  const extractChannelName = (url) => {
    const regex = /youtube\.com\/@([^/?]+)/;
    const match = url.match(regex);
    return match ? match[1] : url;
  };

  // Firestore에 활동 정보를 저장 (users_data > uid > activity)
  const handleAddActivity = async (activity) => {
    if (!uid) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      const channelName = extractChannelName(channelUrl);
      // Firestore 문서 ID는 activity.id (API에서 제공되는 고유값) 사용
      const activityRef = doc(db, "users_data", uid, "activity", activity.id);
      const activityData = {
        title: activity.title,
        date: activity.date.split("T")[0], // "2024-05-30T09:18:24Z" → "2024-05-30"
        description: activity.description || "",
        channel: `[YouTube] ${channelName}`,
        channelType: "YouTube",
        videoId: activity.videoId || "", // 영상 ID (API에서 제공되는 경우)
        //photo: activity.cover, // 썸네일 이미지 URL
        createdAt: new Date().toISOString(),
      };

      await setDoc(activityRef, activityData);
      alert(`"${activity.title}" 활동이 아카이브에 추가되었습니다.`);
    } catch (error) {
      console.error("❌ 활동 저장 실패:", error);
      alert("활동 추가 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className={styleClasses.addActivityPage}>
      <h1>활동 가져오기</h1>

      {/* 유튜브 활동 가져오기 섹션 */}
      <div className={styleClasses.searchBox}>
        <h2>유튜브 활동 가져오기</h2>
        <p>
          유튜브 채널 URL을 입력하면, 해당 채널의 업로드된 모든 동영상 및 쇼츠 활동을 가져옵니다.
        </p>
        <p>예) https://www.youtube.com/@user_ID</p>
        <input
          type="text"
          placeholder="유튜브 채널 URL 입력"
          value={channelUrl}
          onChange={(e) => {setChannelUrl(e.target.value); setPlaylistUrl(""); setVideoUrl("");}}
        />
        <button onClick={fetchActivitiesFromYouTube} disabled={loading}>
          {loading ? "검색 중..." : "활동 검색"}
        </button>
      </div>

      {/* 새로운 섹션: 플레이리스트 및 동영상 URL */}
      <div className={styleClasses.searchBox}>
        <h2>유튜브 활동 가져오기 (플레이리스트/동영상 URL)</h2>
        <p>플레이리스트 URL 혹은 동영상 URL을 입력하면, 해당 활동 정보를 가져옵니다.</p>
        <p>
          예) 플레이리스트: https://www.youtube.com/playlist?list=[플레이리스트ID]<br />
          동영상: https://www.youtube.com/watch?v=[비디오ID]
        </p>
        <div className="formRow">
          <div className={styleClasses.inputField}>
            <input
              type="text"
              placeholder="플레이리스트 URL 입력"
              value={playlistUrl}
              onChange={(e) => {setPlaylistUrl(e.target.value); setChannelUrl(""); setVideoUrl("");}}
            />
          </div>
        </div>
        <div className="formRow">
          <div className={styleClasses.inputField}>
            <input
              type="text"
              placeholder="동영상 URL 입력"
              value={videoUrl}
              onChange={(e) => {setVideoUrl(e.target.value); setPlaylistUrl(""); setChannelUrl("");}}
            />
          </div>
        </div>
        <button onClick={fetchActivitiesFromYouTube} disabled={loading}>
          {loading ? "검색 중..." : "활동 검색"}
        </button>
      </div>

      {/* 활동 목록 나열 */}
      <div className={styleClasses.activityList}>
        {activities.length > 0 ? (
          activities.map((activity) => (
            <div key={activity.id} className={styleClasses.activityCard}>
              {/* 활동 정보 (썸네일 + 제목 + 발행일) */}
              <div className={styleClasses.activityHeader}>
              <img src={`https://img.youtube.com/vi/${activity.videoId}/0.jpg`} alt="활동 썸네일" />
                <p>제목 : {activity.title}</p>
                <p>채널 : {activity.channel}</p>
                <p>발행일 : {activity.date}</p>
              </div>

              {/* 활동 설명 (있을 경우) */}
              {activity.description && (
                <div className={styleClasses.activityDescription}>
                  <p>{activity.description}</p>
                </div>
              )}
              <button onClick={() => handleAddActivity(activity)}>아카이브에 추가</button>
            </div>
          ))
        ) : (
          <p>검색 결과가 없습니다.</p>
        )}
      </div>
    </div>
  );
}

export default ImportActivity;